import React, { useEffect } from "react";
import { Button } from "./Button";

interface ImageLightboxProps {
  imageUrl: string;
  imageAlt: string;
  isOpen: boolean;
  onClose: () => void;
  ctaText?: string;
  onCtaClick?: () => void;
}

export function ImageLightbox({
  imageUrl,
  imageAlt,
  isOpen,
  onClose,
  ctaText,
  onCtaClick,
}: ImageLightboxProps) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
        aria-label="Close"
      >
        <svg
          className="w-8 h-8"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>

      {/* Image container */}
      <div
        className="relative max-w-7xl max-h-[90vh] flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageUrl}
          alt={imageAlt}
          className="max-w-full max-h-[80vh] w-auto h-auto object-contain rounded-lg"
        />

        {/* CTA Button (optional) */}
        {ctaText && onCtaClick && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              onClose();
              onCtaClick();
            }}
            className="!px-6 sm:!px-12 md:!px-16 !py-3 text-base sm:text-lg"
          >
            {ctaText}
          </Button>
        )}
      </div>
    </div>
  );
}
